import { Button, Skeleton } from "@design";
import { RotateCw } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ApiError } from "@/api";
import { Text } from "@/components/Text";

/**
 * Placeholder empty, loading and error states for page sections (cards and lists inside a page,
 * not whole pages). Every section uses these, so the designer's versions replace them here.
 */
export function SectionEmpty({ icon, hint, action }: Readonly<{ icon?: ReactNode; hint: string; action?: ReactNode }>) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-border-subtle border-dashed px-4 py-6 text-center">
      {icon && (
        <span aria-hidden className="text-content-tertiary">
          {icon}
        </span>
      )}
      <Text variant="body-sm" tone="secondary">
        {hint}
      </Text>
      {action}
    </div>
  );
}

export function SectionLoading({ rows = 3 }: Readonly<{ rows?: number }>) {
  const { t } = useTranslation("common");
  return (
    <output aria-label={t("section.loading")} className="flex flex-col gap-3">
      {Array.from({ length: rows }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: identical placeholder rows
        <div key={i} className="flex items-center gap-3">
          <Skeleton variant="avatar" className="size-8 shrink-0" />
          <div className="flex flex-1 flex-col gap-1.5">
            <Skeleton className="h-3 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </div>
      ))}
    </output>
  );
}

/** `error`: what failed, so a rate-limited request (429) says so instead of blaming the server. */
export function SectionError({ onRetry, error }: Readonly<{ onRetry?: () => void; error?: unknown }>) {
  const { t } = useTranslation("common");
  const busy = error instanceof ApiError && error.status === 429;
  return (
    <div role="alert" className="flex flex-col items-center gap-2 rounded-lg bg-error-subtle px-4 py-6 text-center">
      <Text variant="body-sm" className="text-error-on-subtle">
        {t("section.errorTitle")}
      </Text>
      <Text variant="caption" tone="secondary">
        {busy ? t("section.busyBody") : t("section.errorBody")}
      </Text>
      {onRetry && (
        <Button variant="secondary" size="sm" leadingIcon={<RotateCw size={14} />} onClick={onRetry}>
          {t("section.retry")}
        </Button>
      )}
    </div>
  );
}
